import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button, Input, useToast } from '@centraoneplus/ui';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Users,
  Ticket,
  BarChart3,
  Crown,
  X,
  ChevronRight,
  Settings,
  Mail,
  Loader2,
} from 'lucide-react';
import { tenantService } from '@/services/tenant.service';
import { platformTenantService } from '@/services/platform-tenant.service';
import { useTenantContextStore } from '@/stores/tenantContextStore';
import { useAuthStore } from '@stores/authStore';
import type {
  Tenant,
  CreateTenantDto,
  TenantUsage,
  Subscription,
  SubscriptionPlan,
} from '@/types/tenant';
import { ThemedSelect } from '@components/common/ThemedSelect';

const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  free: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  starter: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  professional: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  enterprise: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  suspended: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  trial: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};

// Icon buttons in a tenant row: a 28px target with a hover background.
const ROW_ACTION =
  'p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors';

interface TenantWithDetails extends Tenant {
  subscription?: Subscription;
  usage?: TenantUsage;
}

interface TenantUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  role: string;
  status: string;
}

export function TenantManagement() {
  const toast = useToast();
  const navigate = useNavigate();
  const { setTenant } = useTenantContextStore();
  const { user } = useAuthStore();
  const isPlatform = user?.isPlatformUser;
  const service = isPlatform ? platformTenantService : tenantService;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantWithDetails | null>(null);
  const [saving, setSaving] = useState(false);

  // Tenant users state (detail modal)
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', firstName: '', lastName: '' });
  const [invitingAdmin, setInvitingAdmin] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateTenantDto>({
    name: '',
    slug: '',
    domain: '',
    adminEmail: '',
    plan: 'free',
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await service.getTenants({ search, limit: 50 });
      setTenants(response.items || []);
    } catch {
      toast.error('Error', 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadTenants();
  };

  const handleCreateTenant = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Validation Error', 'Name and slug are required');
      return;
    }

    try {
      setSaving(true);
      const newTenant = await service.createTenant(formData) as any;

      // BFF auto-provisions admin and sends invite email when adminEmail is provided
      if (newTenant.adminProvisioned) {
        if (newTenant.inviteEmailSent) {
          toast.success('Success', `Tenant created and invitation sent to ${formData.adminEmail}`);
        } else {
          toast.success('Tenant Created', 'Tenant created and admin provisioned, but email delivery failed. Use Team Members to resend.');
        }
      } else if (formData.adminEmail) {
        toast.success('Tenant Created', 'Tenant created but invitation failed. Use Team Members in settings to invite.');
      } else {
        toast.success('Success', 'Tenant created successfully');
      }

      setShowCreateModal(false);
      setFormData({ name: '', slug: '', domain: '', adminEmail: '', plan: 'free' });
      loadTenants();
    } catch (error: any) {
      // Close the modal first, then surface the error toast
      setShowCreateModal(false);
      toast.error('Error', error.message || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;

    try {
      setSaving(true);
      await service.updateTenant(selectedTenant.id, {
        name: formData.name,
        domain: formData.domain,
      });
      toast.success('Success', 'Tenant updated successfully');
      setShowEditModal(false);
      loadTenants();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    try {
      if (tenant.status === 'active') {
        await service.deactivateTenant(tenant.id);
        toast.success('Success', 'Tenant deactivated');
      } else {
        await service.activateTenant(tenant.id);
        toast.success('Success', 'Tenant activated');
      }
      loadTenants();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update tenant status');
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!confirm(`Are you sure you want to delete "${tenant.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await service.deleteTenant(tenant.id);
      toast.success('Success', 'Tenant deleted successfully');
      loadTenants();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to delete tenant');
    }
  };

  const handleViewDetails = async (tenant: Tenant) => {
    try {
      const [usage, subscription] = await Promise.all([
        service.getTenantUsage(tenant.id).catch(() => null),
        service.getTenantSubscription(tenant.id).catch(() => null),
      ]);

      setSelectedTenant({
        ...tenant,
        usage: usage || undefined,
        subscription: subscription || undefined,
      });
      setShowDetailModal(true);

      // Load tenant users in the background (platform admin only)
      if (isPlatform) {
        loadTenantUsers(tenant.id);
      }
    } catch {
      setSelectedTenant(tenant);
      setShowDetailModal(true);
    }
  };

  const openEditModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      slug: tenant.slug,
      domain: tenant.domain || '',
      adminEmail: '',
      plan: 'free',
    });
    setShowEditModal(true);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleManageSettings = (tenant: TenantWithDetails) => {
    // Set tenant context with session lock
    setTenant({
      id: tenant.id,
      name: tenant.name,
      slug: tenant.slug,
      plan: tenant.subscription?.plan,
      status: tenant.status,
    });

    // Close the modal and navigate to tenant settings
    setShowDetailModal(false);
    navigate(`/platform/tenants/${tenant.id}/settings/general`);
  };

  const loadTenantUsers = async (tenantId: string) => {
    setLoadingUsers(true);
    try {
      const result = await platformTenantService.getTenantUsers(tenantId, { limit: 10 });
      setTenantUsers(result.data || []);
    } catch {
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Close the invite modal and always reset the form, so reopening it never
  // shows stale values from a cancelled/failed attempt.
  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteData({ email: '', firstName: '', lastName: '' });
  };

  const handleInviteAdmin = async () => {
    if (!selectedTenant || !inviteData.email) return;
    setInvitingAdmin(true);
    try {
      await platformTenantService.provisionTenantAdmin(selectedTenant.id, {
        email: inviteData.email,
        firstName: inviteData.firstName || undefined,
        lastName: inviteData.lastName || undefined,
      });
      toast.success('Success', `Invitation sent to ${inviteData.email}`);
      closeInviteModal();
      loadTenantUsers(selectedTenant.id);
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to send invitation');
    } finally {
      setInvitingAdmin(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Tenant Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage organizations and their subscriptions
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Tenant
        </Button>
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <Input
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>
          Search
        </Button>
      </div>

      {/* Tenants List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No tenants found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Get started by creating your first tenant
          </p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Tenant
          </Button>
        </div>
      ) : (
        // Fixed layout: the four right-hand columns get set widths and Tenant
        // takes the rest, so a long name truncates instead of pushing the
        // table past the card. Narrower than min-w, the table scrolls.
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full min-w-[880px] table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Tenant
                </th>
                <th className="w-28 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th className="w-44 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Subscription Plan
                </th>
                <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Created
                </th>
                <th className="w-44 px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <div className="flex items-center min-w-0">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="ml-4 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={tenant.name}>
                          {tenant.name}
                        </div>
                        <div
                          className="text-sm text-gray-500 dark:text-gray-400 truncate"
                          title={tenant.domain ? `${tenant.slug} • ${tenant.domain}` : tenant.slug}
                        >
                          {tenant.slug}
                          {tenant.domain && ` • ${tenant.domain}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        STATUS_COLORS[tenant.status] || STATUS_COLORS.inactive
                      }`}
                    >
                      {tenant.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                        PLAN_COLORS[tenant.subscriptionTier?.toLowerCase() as SubscriptionPlan] || PLAN_COLORS.free
                      }`}
                    >
                      {tenant.subscriptionTier || 'free'}
                    </span>
                    {/* Only worth a second line when it isn't the normal state. */}
                    {tenant.subscriptionStatus && tenant.subscriptionStatus !== 'active' && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {tenant.subscriptionStatus.replace(/_/g, ' ')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleViewDetails(tenant)}
                        className={`${ROW_ACTION} hover:text-primary-600`}
                        title="View details"
                        aria-label={`View details of ${tenant.name}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(tenant)}
                        className={`${ROW_ACTION} hover:text-blue-600`}
                        title="Edit"
                        aria-label={`Edit ${tenant.name}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(tenant)}
                        className={`${ROW_ACTION} ${
                          tenant.status === 'active' ? 'hover:text-yellow-600' : 'hover:text-green-600'
                        }`}
                        title={tenant.status === 'active' ? 'Deactivate' : 'Activate'}
                        aria-label={`${tenant.status === 'active' ? 'Deactivate' : 'Activate'} ${tenant.name}`}
                      >
                        {tenant.status === 'active' ? (
                          <PowerOff className="w-4 h-4" />
                        ) : (
                          <Power className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(tenant)}
                        className={`${ROW_ACTION} hover:text-red-600`}
                        title="Delete"
                        aria-label={`Delete ${tenant.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal — rendered into document.body so the fixed backdrop
          covers the full viewport even when an ancestor has a transform/filter
          (which would otherwise make `fixed` position relative to it) */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Create New Tenant
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: generateSlug(e.target.value),
                    });
                  }}
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug *
                </label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="acme-corporation"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  URL-friendly identifier (auto-generated from name)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Domain
                </label>
                <Input
                  value={formData.domain || ''}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="acme.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Admin Email
                </label>
                <Input
                  type="email"
                  value={formData.adminEmail || ''}
                  onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                  placeholder="admin@acme.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plan
                </label>
                <ThemedSelect
                  value={formData.plan}
                  onChange={(e) => setFormData({ ...formData, plan: e.target.value as SubscriptionPlan })}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                >
                  <option value="free">Free (evaluation / trial)</option>
                  <option value="growth">Growth (SMB, paid)</option>
                  <option value="professional">Professional (enterprise, Org Brain enabled)</option>
                </ThemedSelect>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Picking <strong>Professional</strong> here auto-enables the
                  Org Brain and Professional-only features after the tenant
                  admin accepts the terms in Settings → Plan.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTenant} disabled={saving}>
                {saving ? 'Creating...' : 'Create Tenant'}
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Edit Tenant
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Domain
                </label>
                <Input
                  value={formData.domain || ''}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTenant} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailModal && selectedTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {selectedTenant.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTenant.slug}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
              {/* Status & Plan */}
              <div className="flex items-center gap-4">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                    STATUS_COLORS[selectedTenant.status] || STATUS_COLORS.inactive
                  }`}
                >
                  {selectedTenant.status}
                </span>
                {selectedTenant.subscription && (
                  <span
                    className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${
                      PLAN_COLORS[selectedTenant.subscription.plan] || PLAN_COLORS.free
                    }`}
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    {selectedTenant.subscription.plan}
                  </span>
                )}
              </div>

              {/* Usage Stats */}
              {selectedTenant.usage && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Usage Statistics
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Users className="w-4 h-4 mr-2" />
                        <span className="text-xs">Users</span>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedTenant.usage.users}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Ticket className="w-4 h-4 mr-2" />
                        <span className="text-xs">Tickets</span>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedTenant.usage.tickets}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <Users className="w-4 h-4 mr-2" />
                        <span className="text-xs">Customers</span>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedTenant.usage.customers}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        <span className="text-xs">API Calls</span>
                      </div>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {(selectedTenant.usage.apiCalls ?? 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Details */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Details
                </h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">ID</dt>
                    <dd className="text-gray-900 dark:text-white font-mono text-xs">
                      {selectedTenant.id}
                    </dd>
                  </div>
                  {selectedTenant.domain && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500 dark:text-gray-400">Domain</dt>
                      <dd className="text-gray-900 dark:text-white">{selectedTenant.domain}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {new Date(selectedTenant.createdAt).toLocaleDateString()}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {new Date(selectedTenant.updatedAt).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Users Section */}
              {isPlatform && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Users
                    </h4>
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Invite Admin
                    </button>
                  </div>
                  {loadingUsers ? (
                    <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                      Loading users...
                    </div>
                  ) : tenantUsers.length === 0 ? (
                    <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      No users yet.{' '}
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Invite an admin
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {tenantUsers.slice(0, 5).map((u) => (
                        <div key={u.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                          <div className="min-w-0">
                            <span className="text-gray-900 dark:text-white font-medium truncate block">
                              {u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                              {u.email}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                              u.role === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                              u.role === 'supervisor' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {u.role}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                              u.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                            }`}>
                              {u.status}
                            </span>
                          </div>
                        </div>
                      ))}
                      {tenantUsers.length > 5 && (
                        <button
                          onClick={() => handleManageSettings(selectedTenant!)}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          View all {tenantUsers.length} users in settings
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-between px-6 py-4 border-t dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => {
                  setShowDetailModal(false);
                  openEditModal(selectedTenant);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={() => handleManageSettings(selectedTenant)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Settings
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Admin Modal */}
      {showInviteModal && selectedTenant && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Invite Admin to {selectedTenant.name}
              </h3>
              <button
                onClick={closeInviteModal}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="admin@company.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    First Name
                  </label>
                  <Input
                    value={inviteData.firstName}
                    onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Last Name
                  </label>
                  <Input
                    value={inviteData.lastName}
                    onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                An invitation email will be sent with a 72-hour setup link.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700">
              <Button variant="outline" onClick={closeInviteModal}>
                Cancel
              </Button>
              <Button onClick={handleInviteAdmin} disabled={!inviteData.email || invitingAdmin}>
                {invitingAdmin ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
